import { useCallback } from 'react';
import { nanoid } from 'nanoid';
import { useReactFlow, getIncomers } from '@xyflow/react';
import { useNodeOperations } from '@/providers/node-operations';
import { handleError } from '@/lib/error/handle';
import { computeUpstreamGridPosition } from '@/lib/nodes/upstream-grid-position';

interface UseImageGuidanceActionsParams {
  id: string;
  t: (key: string) => string;
}

export function useImageGuidanceActions({ id, t }: UseImageGuidanceActionsParams) {
  const { getNode, addEdges, getNodes, getEdges } = useReactFlow();
  const { addNode } = useNodeOperations();

  // 快捷操作：添加图像上传节点并连接
  const handleAddImageNode = useCallback(() => {
    try {
      const currentNode = getNode(id);
      if (!currentNode) return;

      const incomers = getIncomers({ id }, getNodes(), getEdges());
      const position = computeUpstreamGridPosition(currentNode, incomers);

      const newNodeId = addNode('upload', {
        position,
        data: {
          fileType: 'image',
        },
      });

      // 连接新节点到当前图像节点（使用 requestAnimationFrame 确保节点已创建）
      requestAnimationFrame(() => {
        addEdges({
          id: nanoid(),
          source: newNodeId,
          target: id,
        });
      });
    } catch (error) {
      handleError(t('Failed to add image upload node'), error);
    }
  }, [id, getNode, addNode, addEdges, getNodes, getEdges, t]);

  return {
    handleAddImageNode,
  };
}
